import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertPatrolAbsorberBody,
  InsertPatrolSystemAbsorberBody,
  PatrolAbsorber,
  UpdatePatrolAbsorberBody,
  UpdatePatrolSystemAbsorberBody,
} from "./types/absorbers";

export const useAbsorbersApi = () => {
  const api = useApi();

  const useAdminSystemAbsorbers = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId ? `/admin/settings/patrol/systems/${systemId}/absorbers` : `/admin/settings/patrol/absorbers`,
        query,
      );
      return useSWR<Paginated<PatrolAbsorber>, CustomError, string>(url, (url) =>
        api.get<Paginated<PatrolAbsorber>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminAbsorber = useCallback(
    async (body: InsertPatrolAbsorberBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/absorbers`;
      const id = await api.post<{ id: string }, InsertPatrolAbsorberBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const insertAdminSystemAbsorber = useCallback(
    async (systemId: string, body: InsertPatrolSystemAbsorberBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems/${systemId}/absorbers`;
      const id = await api.post<{ id: string }, InsertPatrolSystemAbsorberBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminAbsorber = useCallback(
    async (absorberId: string, body: UpdatePatrolAbsorberBody): Promise<void> => {
      const url = `/admin/settings/patrol/absorbers/${absorberId}`;
      await api.put<UpdatePatrolAbsorberBody>(url, body);
      mutateCache([`/admin/settings/patrol/absorbers`]);
    },
    [api],
  );

  const updateAdminSystemAbsorber = useCallback(
    async (systemId: string, absorberId: string, body: UpdatePatrolSystemAbsorberBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${systemId}/absorbers/${absorberId}`;
      await api.put<UpdatePatrolSystemAbsorberBody>(url, body);
      mutateCache([`/admin/settings/patrol/systems/${systemId}/absorbers`, `/admin/settings/patrol/absorbers`]);
    },
    [api],
  );

  const deleteAdminAbsorber = useCallback(
    async (absorberId: string, systemId?: string): Promise<void> => {
      await api.del(
        systemId
          ? `/admin/settings/patrol/systems/${systemId}/absorbers/${absorberId}`
          : `/admin/settings/patrol/absorbers/${absorberId}`,
      );

      mutateCache([
        `/admin/settings/patrol/absorbers`,
        `/admin/settings/patrol/systems/${systemId}/absorbers`,
        `/admin/settings/patrol/systems/${systemId}/absorbers/${absorberId}`,
      ]);
    },
    [api],
  );

  const updateAdminAbsorberVisibility = useCallback(
    async (absorberId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/absorbers/${absorberId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/absorbers"]);
    },
    [api],
  );

  const updateAdminAbsorberOrder = useCallback(
    async (absorberId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/absorbers/${absorberId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/absorbers"]);
    },
    [api],
  );

  return {
    useAdminSystemAbsorbers,
    insertAdminAbsorber,
    insertAdminSystemAbsorber,
    updateAdminAbsorber,
    updateAdminSystemAbsorber,
    updateAdminAbsorberVisibility,
    updateAdminAbsorberOrder,
    deleteAdminAbsorber,
  };
};
